const ATTRIBUTE_ENTITY_TYPES = ['measuring_point', 'project', 'user'] as const;
export type AttributeEntityType = (typeof ATTRIBUTE_ENTITY_TYPES)[number];

export type AttributeValueType = 'text' | 'image' | 'image_list';

export type AttributeEntityGraphQLValue = 'MEASURING_POINT' | 'PROJECT' | 'USER';
export type AttributeValueGraphQLValue = 'TEXT' | 'IMAGE' | 'IMAGE_LIST';

export interface AttributeAsset {
    id: string;
    url: string;
    name: string | null;
    caption: string;
    sortOrder: number | null;
}

interface BaseAttributeEntry {
    definitionId: string;
    key: string;
    label: string;
    valueType: AttributeValueType;
    updatedAt: string | null;
}

export interface TextAttributeEntry extends BaseAttributeEntry {
    valueType: 'text';
    textValue: string;
}

export interface ImageAttributeEntry extends BaseAttributeEntry {
    valueType: 'image';
    image: AttributeAsset | null;
}

export interface ImageListAttributeEntry extends BaseAttributeEntry {
    valueType: 'image_list';
    imageList: AttributeAsset[];
}

export type AttributeEntry = TextAttributeEntry | ImageAttributeEntry | ImageListAttributeEntry;

interface AttributeEntityMeta {
    routeSegment: AttributeEntityType;
    gqlValue: AttributeEntityGraphQLValue;
}

const ATTRIBUTE_ENTITY_META: Record<AttributeEntityType, AttributeEntityMeta> = {
    measuring_point: {
        routeSegment: 'measuring_point',
        gqlValue: 'MEASURING_POINT',
    },
    project: {
        routeSegment: 'project',
        gqlValue: 'PROJECT',
    },
    user: {
        routeSegment: 'user',
        gqlValue: 'USER',
    },
};

export function isAttributeEntityType(value: unknown): value is AttributeEntityType {
    return (
        typeof value === 'string' && ATTRIBUTE_ENTITY_TYPES.includes(value as AttributeEntityType)
    );
}

export function toAttributesEntityGraphQLValue(
    entityType: AttributeEntityType
): AttributeEntityGraphQLValue {
    return ATTRIBUTE_ENTITY_META[entityType].gqlValue;
}

export function formatAttributeEntityType(entityType: AttributeEntityType): string {
    if (entityType === 'measuring_point') {
        return gettext('MEASURING_POINT');
    }

    if (entityType === 'project') {
        return gettext('PROJECT');
    }

    return gettext('ATTRIBUTE_ENTITY_ACCOUNT');
}

export function normalizeAttributeValueType(value: string): AttributeValueType {
    const mapping: Record<AttributeValueGraphQLValue | AttributeValueType, AttributeValueType> = {
        TEXT: 'text',
        IMAGE: 'image',
        IMAGE_LIST: 'image_list',
        text: 'text',
        image: 'image',
        image_list: 'image_list',
    };
    const normalized = mapping[value as keyof typeof mapping];

    if (!normalized) {
        throw new Error(`Unknown Attributes value type: ${value}`);
    }

    return normalized;
}

export function formatAttributeValueType(valueType: AttributeValueType): string {
    const labels: Record<AttributeValueType, string> = {
        text: gettext('ATTRIBUTE_VALUE_TYPE_TEXT'),
        image: gettext('ATTRIBUTE_VALUE_TYPE_IMAGE'),
        image_list: gettext('ATTRIBUTE_VALUE_TYPE_IMAGE_LIST'),
    };

    return labels[valueType];
}

export function assertNeverAttributeEntry(entry: never): never {
    throw new Error(`Unhandled Attributes entry: ${JSON.stringify(entry)}`);
}
